import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageSquare, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "../lib/leadsApi";

const FAQ = [
  { q: "How do I book a test ride?",
    a: "Use the 'Book Test Ride' button on any page. Pick your city, model and a preferred slot — our store team will WhatsApp confirmation in under 2 hours." },
  { q: "Is the battery removable on all models?",
    a: "Yes. Every model in the eBikeGo line-up ships with a removable lithium-ion pack you can charge at home, office or any 5-amp socket." },
  { q: "Do I need a license to ride?",
    a: "No. All eBikeGo e-cycles are speed-capped at 25 km/h with a 250W motor, which is exempt from licensing under Indian rules." },
  { q: "What's covered under warranty?",
    a: "Lifetime warranty on the frame and 2 years on the motor. Detailed terms are shared at delivery." },
  { q: "Where can I service my e-cycle?",
    a: "At any of the 65+ eBikeGo stores or 95+ authorised service centres across India. Schedule from the Service page." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", topic: "General enquiry", message: "" });
  const [open, setOpen] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone so we can reach you.");
      return;
    }
    toast.success("Thanks — we'll be in touch within 24 hours.", {
      description: `Topic: ${form.topic} • ${form.city || "City TBD"}`,
    });
    submitLead({
      type: "enquiry",
      name: form.name, email: form.email, phone: form.phone,
      city: form.city, topic: form.topic, message: form.message,
    });
    setForm({ name: "", email: "", phone: "", city: "", topic: "General enquiry", message: "" });
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="relative pt-16 pb-16 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative">
          <p className="eyebrow">Contact</p>
          <h1 className="font-outfit text-5xl lg:text-7xl font-bold mt-5 leading-[0.98] tracking-tighter max-w-3xl text-slate-900">
            Let's talk e-cycles.
          </h1>
          <p className="text-slate-600 text-lg mt-6 max-w-2xl">
            Questions on a model, a rental plan or a franchise opportunity? Drop a note — every enquiry gets a response within 24 hours.
          </p>
        </div>
      </section>

      <section className="container-x py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12">
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_18px_50px_rgba(11,19,32,0.04)]" data-testid="contact-form">
          <p className="eyebrow">Send a message</p>
          <h2 className="font-outfit text-3xl font-bold mt-3 text-slate-900">Drop us a note</h2>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} name="name" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} name="phone" />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} name="email" type="email" />
            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} name="city" />
            <label className="col-span-2 block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Topic</span>
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                data-testid="contact-topic"
                className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
              >
                <option>General enquiry</option>
                <option>Buy an e-cycle</option>
                <option>Rental plan</option>
                <option>Service request</option>
                <option>Franchise / Dealership</option>
                <option>Press / Partnerships</option>
              </select>
            </label>
            <label className="col-span-2 block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Message</span>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                data-testid="contact-message"
                className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none resize-none"
                placeholder="Tell us how we can help…"
              />
            </label>
          </div>
          <button type="submit" className="btn-primary mt-6" data-testid="contact-submit">
            Send Message
          </button>
        </form>

        <aside className="space-y-4">
          <ContactCard icon={<Phone size={18} />} label="Call us" value="+91 89767 73152" href="tel:+918976773152" />
          <ContactCard icon={<MessageSquare size={18} />} label="WhatsApp" value="+91 89767 73152" href="https://wa.me/918976773152" />
          <ContactCard icon={<Mail size={18} />} label="Email" value="info@ebikego.in" href="mailto:info@ebikego.in" />
          <ContactCard icon={<MapPin size={18} />} label="HQ"
            value="Pramukh Plaza, Cardinal Gracious Rd, Andheri East, Mumbai 400099" />
        </aside>
      </section>

      <section id="faq" className="container-x pb-24">
        <p className="eyebrow">FAQs</p>
        <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
          Things riders ask us.
        </h2>
        <div className="mt-10 border-t border-slate-200" data-testid="faq-list">
          {FAQ.map((f, i) => (
            <div key={i} className="border-b border-slate-200">
              <button
                className="w-full flex items-center justify-between py-6 text-left"
                onClick={() => setOpen(open === i ? null : i)}
                data-testid={`faq-${i}`}
              >
                <span className="font-outfit text-lg lg:text-xl font-bold text-slate-900">{f.q}</span>
                <ChevronDown
                  size={20}
                  className={`text-[#1E6FE5] transition-transform ${open === i ? "rotate-180" : ""}`}
                />
              </button>
              <motion.div
                initial={false}
                animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                className="overflow-hidden"
              >
                <p className="text-slate-600 leading-relaxed pb-6 max-w-3xl">{f.a}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, name, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`contact-${name}`}
        className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none placeholder:text-slate-400"
      />
    </label>
  );
}

function ContactCard({ icon, label, value, href }) {
  const inner = (
    <div className="border border-slate-200 bg-white rounded-2xl p-6 hover:border-[#2A8BFF]/40 hover:shadow transition flex items-start gap-4">
      <span className="w-10 h-10 rounded-full bg-[#2A8BFF]/10 text-[#1E6FE5] inline-flex items-center justify-center shrink-0">{icon}</span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</p>
        <p className="text-slate-900 text-base mt-1.5 font-semibold">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
